/**
 * Activity Manager Module
 *
 * Tracks user activity for session management. Activity is stored in
 * localStorage to be shared across tabs and compared against token
 * refresh timestamps to determine if user is active.
 *
 * Usage:
 *   const activity = createActivityManager({
 *     activityKey: 'bb_last_activity_timestamp',
 *     refreshKey: 'bb_last_refresh_timestamp',
 *     channelName: 'bb_auth0_activity',
 *     debounceMs: 30000,
 *   });
 *
 *   activity.initialize(() => isDialogOpen);
 *   activity.isUserActive(); // true/false
 *   activity.reset(); // user clicked "Continue Working"
 */

import { readTimestamp, writeTimestamp } from './storageUtils';
import { broadcast } from './sessionChannelManager';
import { sessionLog } from './sessionLog';
import { getTokenIssuedAt } from './tokenUtils';
import {
  ACTIVITY_DEBOUNCE_MS,
  MESSAGE_TYPES,
  ONE_YEAR_MS,
  CLOCK_SKEW_TOLERANCE_MS,
  STALE_ACTIVITY_TOLERANCE_MS,
} from '../sessionConfig';

/**
 * Create an activity manager instance
 *
 * @param {Object} config
 * @param {string} config.activityKey - localStorage key for activity timestamp
 * @param {string} config.refreshKey - localStorage key for refresh timestamp
 * @param {string} config.channelName - BroadcastChannel name for activity sync
 * @param {number} [config.debounceMs] - Debounce duration for activity updates
 * @param {string} [config.logPrefix='[Activity]'] - Log prefix for debugging
 * @returns {Object} Activity manager
 */
export const createActivityManager = ({
  activityKey,
  refreshKey,
  channelName,
  debounceMs = ACTIVITY_DEBOUNCE_MS,
  logPrefix = '[Activity]',
}) => {
  // Internal state
  let debounceTimeout = null;
  let listenersInitialized = false;
  let activityHandlers = null;
  let getIsDialogOpenFn = () => false;
  let sessionInitialized = false; // Tracks if initializeFromToken has run this session

  /**
   * Update activity timestamp (debounced)
   * @param {string} source - Source of activity (e.g., 'mousemove', 'keydown')
   */
  const updateActivity = (source = 'unknown') => {
    // Don't track activity when dialog is open
    if (getIsDialogOpenFn()) return;

    // Skip if in debounce period
    if (debounceTimeout) return;

    const now = Date.now();
    writeTimestamp(activityKey, now);

    sessionLog(`${logPrefix} Activity detected`, { source, timestamp: now });

    // Broadcast to other tabs
    broadcast(channelName, {
      type: MESSAGE_TYPES.ACTIVITY_DETECTED,
      timestamp: now,
      source,
    });

    // Set debounce
    debounceTimeout = setTimeout(() => {
      debounceTimeout = null;
    }, debounceMs);
  };

  /**
   * Check if user is active (has interacted since last refresh)
   * @returns {boolean}
   */
  const isUserActive = () => {
    const activity = readTimestamp(activityKey, 0);
    const refresh = readTimestamp(refreshKey, 0);

    // If no refresh timestamp, session hasn't been initialized
    if (refresh === 0) {
      return false;
    }

    return activity > refresh;
  };

  /**
   * Check if user is active relative to a token's issued-at time.
   * This is the Auth0-safe version that uses server-side timestamp as source of truth,
   * eliminating cross-tab race conditions from using local Date.now() for refresh timestamp.
   *
   * IMPORTANT: Includes clock skew tolerance because:
   * - Token IAT is server time (Auth0's clock)
   * - Activity timestamp is client time (user's browser)
   * - If client clock is behind server, activity appears to be "before" token was issued
   *
   * @param {number} iatMs - Token's issued-at timestamp in milliseconds
   * @returns {boolean} True if user has been active since the token was issued
   */
  const isUserActiveForToken = (iatMs) => {
    if (!iatMs || iatMs <= 0) {
      // No IAT available - fall back to standard check
      return isUserActive();
    }

    const activity = readTimestamp(activityKey, 0);

    // If no activity recorded, user is not active
    if (activity === 0) {
      return false;
    }

    // User is active if they've had activity AFTER the token was issued (with tolerance)
    // CLOCK_SKEW_TOLERANCE_MS handles minor clock drift between client and server
    return activity > iatMs - CLOCK_SKEW_TOLERANCE_MS;
  };

  /**
   * Reset both timestamps (user clicked "Continue Working")
   */
  const resetTimestamps = () => {
    const now = Date.now();
    writeTimestamp(refreshKey, now);
    writeTimestamp(activityKey, now + 1); // Ensure activity > refresh

    broadcast(channelName, {
      type: MESSAGE_TYPES.SESSION_RESET,
      timestamp: now,
    });

    sessionLog(`${logPrefix} 🔄 Reset timestamps (user confirmed activity)`);
  };

  /**
   * Update only refresh timestamp (auto-refresh)
   * @param {number} timestamp - Timestamp (default: now)
   */
  const updateRefreshTimestamp = (timestamp = Date.now()) => {
    writeTimestamp(refreshKey, timestamp);

    broadcast(channelName, {
      type: MESSAGE_TYPES.TOKEN_REFRESHED,
      timestamp,
    });
  };

  /**
   * Initialize from token's IAT claim (only on first load)
   * @returns {boolean} True if initialized successfully
   */
  const initializeFromToken = () => {
    // Only run once per browser session to avoid resetting activity on token refreshes
    if (sessionInitialized) {
      return true;
    }

    const iatMs = getTokenIssuedAt();
    if (!iatMs) return false;

    const storedRefresh = readTimestamp(refreshKey, 0);
    const storedActivity = readTimestamp(activityKey, 0);
    const now = Date.now();

    // Initialize refresh timestamp if not set or invalid
    const oneYearFromNow = now + ONE_YEAR_MS;
    if (storedRefresh === 0 || storedRefresh > oneYearFromNow) {
      writeTimestamp(refreshKey, iatMs);
    }

    // Initialize activity timestamp if:
    // 1. Not set at all (first load)
    // 2. Older than the token's IAT minus tolerance (stale from previous session)
    // This handles the case where user had an old session, storage wasn't fully cleared,
    // and now has a new token - we need activity to be "fresh" relative to new token.
    const isActivityStale =
      storedActivity === 0 || storedActivity < iatMs - STALE_ACTIVITY_TOLERANCE_MS;

    if (isActivityStale) {
      writeTimestamp(activityKey, now);
      sessionLog(`${logPrefix} Initialized activity timestamp`, {
        reason: storedActivity === 0 ? 'first_load' : 'stale_from_previous_session',
        oldActivity: storedActivity,
        tokenIat: iatMs,
        newActivity: now,
      });
    }

    // Mark as initialized to prevent resetting on token refreshes
    sessionInitialized = true;
    return true;
  };

  /**
   * Create activity event handlers
   * @returns {Object} Event handlers
   */
  const createHandlers = () => {
    if (activityHandlers) return activityHandlers;

    activityHandlers = {
      mousemove: () => updateActivity('mousemove'),
      mousedown: () => updateActivity('mousedown'),
      click: () => updateActivity('click'),
      keydown: () => updateActivity('keydown'),
      keypress: () => updateActivity('keypress'),
      touchstart: () => updateActivity('touchstart'),
      touchmove: () => updateActivity('touchmove'),
      message: (event) => {
        // Handle postMessage from iframes (OnlyOffice, etc.)
        if (event.data?.bbActivity === true) {
          updateActivity('iframe');
        } else if (
          event.data?.type === 'userInteraction' &&
          (event.origin?.includes('blackboiler') || event.origin?.includes('onlyoffice'))
        ) {
          updateActivity('iframe');
        }
      },
    };

    return activityHandlers;
  };

  /**
   * Initialize activity listeners
   * @param {Function} getIsDialogOpen - Function that returns whether dialog is open
   */
  const initializeListeners = (getIsDialogOpen) => {
    if (listenersInitialized) return;

    getIsDialogOpenFn = getIsDialogOpen || (() => false);
    const eventOptions = { passive: true, capture: false };
    const handlers = createHandlers();

    window.addEventListener('mousemove', handlers.mousemove, eventOptions);
    window.addEventListener('mousedown', handlers.mousedown, eventOptions);
    window.addEventListener('click', handlers.click, eventOptions);
    window.addEventListener('keydown', handlers.keydown, eventOptions);
    window.addEventListener('keypress', handlers.keypress, eventOptions);
    window.addEventListener('touchstart', handlers.touchstart, eventOptions);
    window.addEventListener('touchmove', handlers.touchmove, eventOptions);
    window.addEventListener('message', handlers.message, eventOptions);

    listenersInitialized = true;
  };

  /**
   * Get last activity timestamp
   * @returns {number}
   */
  const getLastActivity = () => readTimestamp(activityKey, 0);

  /**
   * Get last refresh timestamp
   * @returns {number}
   */
  const getLastRefresh = () => readTimestamp(refreshKey, 0);

  /**
   * Reset module state (call on logout)
   */
  const reset = () => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      debounceTimeout = null;
    }
    sessionInitialized = false; // Allow re-initialization on next login
  };

  /**
   * Signal external activity (e.g., from parent app)
   * @param {string} source - Activity source identifier
   */
  const signalActivity = (source = 'external') => {
    updateActivity(source);
  };

  return {
    updateActivity,
    isUserActive,
    isUserActiveForToken,
    resetTimestamps,
    updateRefreshTimestamp,
    initializeFromToken,
    initializeListeners,
    getLastActivity,
    getLastRefresh,
    reset,
    signalActivity,
  };
};

export default createActivityManager;
